import { IDataRedFlag } from "@/components/RedFlagModal";
import { createRedFlag, updateRedFlag, sendSlackMessage } from "@/graphql/mutations";
import { GraphQLQuery } from "@aws-amplify/api";
import { API, graphqlOperation } from "aws-amplify";
import { uploadFile } from "./useUploadFile";
import { useQuery, useMutation  } from "@tanstack/react-query";
import { getRedFlagDetail, getRedFlagReportCount, listRedFlags } from "@/graphql/queries";
import dayjs from "dayjs";

interface IDataCreateRedFlag {
  input: {
    description: string;
    attachmentUrl: string;
    referenceType: "EVENT" | "CIRCLE" | "CONNECTION" | "ENDORSEMENT";
    referenceId: string;
  };
  condition?: string;
}

interface CreateRedFlagMutation {
  createRedFlag: IDataCreateRedFlag;
}

interface IDataCustomerReport {
  title: string;
  description: string;
  attachmentUrl?: string;
  isAnonymous?: boolean;
}

interface IDataRequestChange {
  issues: string[];
  description: string;
  attachmentUrls?: string[];
}


interface CreateCustomerReportResponse {
  createCustomerReport: any; // Adjust the return type based on your schema
}

export const createRedFlagForm = async (payload: IDataRedFlag) => {
  async function setRedFlag(attachmentKey: string | null | undefined) {
    await API.graphql<GraphQLQuery<CreateRedFlagMutation>>({
      query: createRedFlag,
      variables: {
        input: {
          issue: payload.issue,
          description: payload.description,
          attachmentUrl: attachmentKey,
          referenceType: payload.referenceType,
          referenceId: payload.referenceId,
          isAnonymous: payload.isAnonymous,
        },
      },
    });
  }

  if (payload.fileUpload) {
    const key = `redflag/${payload.referenceType}/${Date.now()}-${payload.fileUpload.name}`;
    await uploadFile({
      key,
      file: payload.fileUpload,
    });
    await setRedFlag(key);
    return key;
  } else {
    await setRedFlag(null);
    return null;
  }
};

export const createCustomerReportForm = async (payload: IDataCustomerReport) => {
  const response = await API.graphql<GraphQLQuery<CreateCustomerReportResponse>>({
    query: sendSlackMessage,
    variables: {
      title: payload.title,
      description: payload.description,
      type: "CUSTOMER_REPORT",
      attachmentUrls: [payload.attachmentUrl],
      isAnonymous: payload.isAnonymous,
    },
  });

  return response;
};

export const createChangeTnCOrganization = async (payload: IDataRequestChange) => {
  const response = await API.graphql<GraphQLQuery<CreateCustomerReportResponse>>({
    query: sendSlackMessage,
    variables: {
      title: `Change ${payload.issues.join(",")}`,
      description: payload.description,
      type: "ORG_SETTING_EDIT_REQUEST",
      attachmentUrls: payload.attachmentUrls,
      isAnonymous: false,
    },
  });

  return response;
};

/*  LIST RED FLAG */
const fetchListofRedFlag = async (id?: string) => {
  const response: any = await API.graphql(graphqlOperation(listRedFlags));
  const records: any = response?.data ?? [];
  const sorted = records?.listRedFlags?.items.sort(
    (a: any, b: any) => (dayjs(b?.updatedAt) as any) - (dayjs(a?.updatedAt) as any),
  );

  return {
    listRedFlags: {
      items: sorted,
    },
  };
};

export const useListRedFlag = (id?: string) => {
  return useQuery({
    queryKey: [`KEY_GET_RED_FLAGS`],
    queryFn: () => fetchListofRedFlag(id),
  });
};

interface IRedflagReport {
  month: string;
  total: number;
}

const fetchRedFlagReport = async () => {
  const response: any = await API.graphql(graphqlOperation(getRedFlagReportCount));
  const records: IRedflagReport[] = response?.data?.getRedFlagReportCount ?? [];
  return records;
};

export const useRedFlagReport = () => {
  return useQuery({
    queryKey: [`KEY_GET_RED_FLAGS_REPORT`],
    queryFn: () => fetchRedFlagReport(),
  });
};

interface IGetRedFlagDetailResponse {
  redFlag: {
    issue: string;
    id: string;
    organizationGroup: string;
    description: string;
    attachmentUrl: string;
    referenceType: string;
    comment: any;
    comments: any;
    referenceId: string;
    referenceName: string;
    reporter: {
      id: string;
      cognitoID: string;
      email: string;
      organizationGroup: string;
      firstName: string;
      lastName: string;
      username: string;
      avatarUrl: string;
      bannerImageUrl: string;
      bio: string;
      jobTitle: string;
      type: string;
      lookingForType: string;
      joinedAt: string;
      phone: string;
      points: number;
      dateOfBirth: string;
      focusedSkillTrack: {
        id: string;
        organizationGroup: string;
        departmentId: string;
        skillTrackTagId: string;
        color: string;
        createdAt: string;
        updatedAt: string;
        _version: number;
        _deleted: boolean;
        _lastChangedAt: string;
      };
      minimumIntroducers: number;
      interest: string;
      expertises: string[];
      department: {
        id: string;
        name: string;
        description: string;
        organizationGroup: string;
        createdAt: string;
        updatedAt: string;
        _version: number;
        _deleted: boolean;
        _lastChangedAt: string;
      };
      almaMater: string;
      titleRank: string;
      awards: string[];
      gender: string;
      race: string;
      ethnicity: string;
      sexuality: string;
      vegetarian: boolean;
      vegan: boolean;
      personalityType: string;
      religion: string;
      class: string;
      relationshipStatus: string;
      taxBracket: string;
      birthCountry: string;
      birthCity: string;
      childhoodZip: string;
      currentZip: string;
      dogOwner: boolean;
      catOwner: boolean;
      moonSign: string;
      sunSign: string;
      artist: string;
      musicalInstrument: string;
      morningVsNightPerson: string;
      generation: string;
      hasSiblings: boolean;
      totalSiblings: number;
      birthOrder: string;
      favorites: {
        faveMusicGenre: string;
        faveSinger: string;
        faveMuseum: string;
        faveSportTeam: string;
        faveSport: string;
        faveTVShow: string;
        faveBoardGame: string;
        faveVideoGame: string;
        faveBook: string;
        faveTheatreShow: string;
        faveVacation: string;
        faveNewsSource: string;
        faveCity: string;
        faveRestaurant: string;
        faveBar: string;
        faveNightClub: string;
        favePoliticalParty: string;
        faveCharities: string[];
      };
      heroes: string[];
      sages: string[];
      celebsLoveToMeet: string[];
      introVsExtro: string;
      eatOutVsEatIn: string;
      createdAt: string;
      updatedAt: string;
      _version: number;
      _deleted: boolean;
      _lastChangedAt: string;
      userFocusedSkillTrackId: string;
      userDepartmentId: string;
    };
    status: string;
    review: string;
    isCompleted: boolean;
    completedAt: string;
    createdAt: string;
    updatedAt: string;
    _version: number;
    _deleted: boolean;
    _lastChangedAt: string;
    redFlagReporterId: string;
  };
  imageUrlReference: string;
}

const fetchRedflagDetail = async (id: string) => {
  const response: any = await API.graphql(
    graphqlOperation(getRedFlagDetail, {
      redFlagId: id,
    }),
  );
  const records: IGetRedFlagDetailResponse = response?.data?.getRedFlagDetail ?? {};

  return records;
};

export const useRedFlagDetail = (id: string) => {
  return useQuery({
    queryKey: [`KEY_GET_RED_FLAGS_DETAIL`],
    queryFn: () => fetchRedflagDetail(id),
    enabled: false,
  });
};


export const useCreateCustomerReport = () => {
  return useMutation({
    mutationFn: createCustomerReportForm,
  });
};

interface IUpdateRedFlagHR {
  status: "INPROGRESS" | "CLOSED" | "OPEN";
  id: string;
  _version: number;
  isCompleted?: boolean;
  comment?: string;
  review?: string;
  comments?: {
    message: string;
    name: string;
    createdAt: string;
    review: string;
  }[];
}

export const updateRedFlagHR = async (payload: IUpdateRedFlagHR) => {
  const records = await API.graphql<GraphQLQuery<IUpdateRedFlagHR>>({
    query: updateRedFlag,
    variables: { input: payload },
  });

  return records;
};
